import { useContext, useState, useEffect } from 'react';
import AuthContext from 'contexts/AuthContext';
import { useDispatch } from 'react-redux';

// 3rd party
import { Buffer } from 'buffer';

// project imports
import { logout as accountLogout, login as accountLogin } from 'store/slices/accountSlice';
import { logout as projectLogout } from 'store/slices/projectSlice';
import { logout as titleLogout } from 'store/slices/titleSlice';
import { logout as registrationLogout } from 'store/slices/registrationSlice';
import { logout as adminLogout } from 'store/slices/adminSlice';
import { logout as ccbLogout } from 'store/slices/ccbSlice';
import { logout as attorneyLogout } from 'store/slices/attorneySlice';
import { logout as eventLogout } from 'store/slices/eventSlice';
import { SET_TITAN, SET_DEFAULT } from 'store/slices/customizationSlice';
import { titleApi } from 'store/api/titleApi';
import { registrationApi } from 'store/api/registrationApi';
import { projectApi } from 'store/api/projectApi';

// APIs
import {
    loginAPI,
    loginChangePasswordAPI,
    registerUserAPI,
    registerUserConfirmAPI,
    registerResendCodeAPI,
    forgotPasswordAPI,
    forgotPasswordConfirmAPI
} from 'requests/auth';

const setSession = ({ idToken, refreshToken }) => {
    if (idToken) {
        localStorage.setItem('idToken', idToken);
    } else {
        localStorage.removeItem('idToken');
    }
    if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
    } else {
        localStorage.removeItem('refreshToken');
    }
};

// ==============================|| AUTH HOOKS ||============================== //
const useAuth = () => useContext(AuthContext);

const checkAdmin = (token) => {
    const base64Payload = token.split('.')[1];
    let payload = new Uint8Array();

    try {
        payload = Buffer.from(base64Payload, 'base64');
    } catch (err) {
        throw new Error(`parseJwt# Malformed token: ${err}`);
    }
    const jwtObject = JSON.parse(payload);
    const groups = jwtObject['cognito:groups'];
    if (!groups) {
        return false;
    }
    return groups.includes('Admin');
};

const isTokenExpired = (token) => {
    if (!token) {
        return true;
    }

    try {
        const base64Payload = token.split('.')[1];
        const payload = Buffer.from(base64Payload, 'base64');
        const jwtObject = JSON.parse(payload);

        // JWT exp is in seconds, Date.now() is in milliseconds
        const expirationTime = jwtObject.exp * 1000;
        const currentTime = Date.now();

        // Only consider token expired if it has actually expired
        // The backend refresh logic will handle token refresh automatically
        return currentTime >= expirationTime;
    } catch (err) {
        console.error('Error checking token expiration:', err);
        return true;
    }
};

const useAuthProvider = () => {
    const dispatch = useDispatch();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [idToken, setIdToken] = useState('');

    console.log('INIT AUTH PROVIDER');

    useEffect(() => {
        const init = async () => {
            try {
                console.log('Check Refresh');
                // After a refresh if we have a valid token => keep calm and carry on
                if (localStorage.getItem('idToken')) {
                    setIsLoggedIn(true);
                    setIdToken(localStorage.getItem('idToken'));
                    return;
                }
                // In all other cases logout
                dispatch(accountLogout());
            } catch (err) {
                console.error(err);
                dispatch(accountLogout());
            }
        };
        init();
    }, [dispatch]);

    // Check token validity when user returns to the tab
    useEffect(() => {
        const handleWindowFocus = async () => {
            console.log('Window focused - checking token validity');
            const token = localStorage.getItem('idToken');

            // If user is supposed to be logged in but token is expired
            if (isLoggedIn && isTokenExpired(token)) {
                console.log('Token expired on focus - attempting to refresh token');

                try {
                    // Try to refresh the token using the refresh token
                    const refreshToken = localStorage.getItem('refreshToken');
                    if (!refreshToken) {
                        throw new Error('No refresh token available');
                    }

                    const DOMAIN = process.env.REACT_APP_API_URL;
                    const loginUrl = `${DOMAIN}auth/login?refreshToken=${refreshToken}`;
                    const authResponse = await fetch(loginUrl, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    if (authResponse.status === 200) {
                        const jsonAuthResponse = await authResponse.json();
                        if (jsonAuthResponse.success && jsonAuthResponse.data.idToken) {
                            // Successfully refreshed token
                            console.log('Token refreshed successfully on focus');
                            const newIdToken = jsonAuthResponse.data.idToken;
                            localStorage.setItem('idToken', newIdToken);
                            setIdToken(newIdToken);
                            // Keep user logged in
                            return;
                        }
                    }

                    // If we reach here, refresh failed
                    throw new Error('Token refresh failed');
                } catch (err) {
                    console.error('Failed to refresh token on focus:', err);
                    // Clear session and logout
                    setIsLoggedIn(false);
                    setSession({});
                    // Dispatch logout actions to all slices
                    dispatch(accountLogout());
                    dispatch(projectLogout());
                    dispatch(titleLogout());
                    dispatch(registrationLogout());
                    dispatch(adminLogout());
                    dispatch(ccbLogout());
                    dispatch(attorneyLogout());
                    dispatch(eventLogout());
                    dispatch(titleApi.util.resetApiState());
                    dispatch(registrationApi.util.resetApiState());
                    dispatch(projectApi.util.resetApiState());
                    // Redirect to login
                    window.location.href = '/login';
                }
            }
        };

        // Add event listener for window focus
        window.addEventListener('focus', handleWindowFocus);

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [isLoggedIn, dispatch]); // Re-run if login status changes

    const login = async ({ email, password }) => {
        const loginResponse = await loginAPI({ userName: email, password });
        const { idToken, refreshToken, userData, feeData } = loginResponse;
        setSession({ idToken, refreshToken });
        setIsLoggedIn(true);
        const isAdmin = checkAdmin(idToken);
        console.log('>>>>Log in', userData);
        dispatch(accountLogin({ userData, feeData, isAdmin }));
        if (userData.isTitan) {
            console.log('>>>>TITAN');
            dispatch(SET_TITAN());
        } else {
            console.log('>>>>DEFAULT');
            dispatch(SET_DEFAULT());
        }
    };

    const register = async ({ email, password, givenName, familyName, profession }) =>
        registerUserAPI({ email, password, givenName, familyName, profession });

    const registerConfirm = async ({ userName, confirmationCode }) => {
        const response = await registerUserConfirmAPI({ userName, confirmationCode });
        return response;
    };

    const registerResendCode = async ({ userName }) => {
        const response = await registerResendCodeAPI({ userName });
        return response;
    };

    const forgotPassword = async ({ userName }) => {
        const response = await forgotPasswordAPI({ userName });
        return response;
    };
    const forgotPasswordConfirm = async ({ userName, confirmationCode, password }) => {
        const response = await forgotPasswordConfirmAPI({ userName, confirmationCode, password });
        return response;
    };

    const logout = () => {
        setIsLoggedIn(false);
        setSession({});
        dispatch(accountLogout());
        dispatch(projectLogout());
        dispatch(titleLogout());
        dispatch(registrationLogout());
        dispatch(adminLogout());
        dispatch(ccbLogout());
        dispatch(attorneyLogout());
        dispatch(eventLogout());
        dispatch(titleApi.util.resetApiState());
        dispatch(registrationApi.util.resetApiState());
        dispatch(projectApi.util.resetApiState());
    };

    const loginChangePassword = async ({ userID, session, password }) => {
        await loginChangePasswordAPI({ userID, session, password });
        return true;
    };

    return {
        forgotPassword,
        forgotPasswordConfirm,
        login,
        loginChangePassword,
        logout,
        register,
        registerConfirm,
        registerResendCode,
        isLoggedIn,
        idToken
    };
};

export default useAuth;
export { useAuthProvider };
